import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  Divider,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ListAlt as FlowsIcon,
  Inbox as InboxIcon,
  Forum as ForumIcon,
  EventAvailable as BookingsIcon,
  Insights as InsightsIcon,
  Feedback as FeedbackIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  ChevronLeft as CollapseIcon,
  ChevronRight as ExpandIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLiveBooking } from '../../hooks/useLiveBooking';
import { usePreferences } from '../../contexts/PreferencesContext';
import BrandMark from '../common/BrandMark';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import WorkspaceSetupDialog from '../onboarding/WorkspaceSetupDialog';
import OrganizationSwitcher from '../organization/OrganizationSwitcher';
import EnvironmentSwitcher from '../environment/EnvironmentSwitcher';
import FeedbackDialog from '../feedback/FeedbackDialog';
import PlanLimitDialog from '../billing/PlanLimitDialog';
import { AD_ATTRIBUTION_ENABLED } from '../../features';

/** Wide enough for the switchers to breathe; the old 240 left them cramped. */
const drawerWidth = 280;
/** Icons only. Enough to stay a tap target, narrow enough to give the canvas the screen. */
const railWidth = 72;

const EXPANDED_KEY = 'flowgate.nav.expanded';

/** The small grey caption above each switcher. */
const SWITCHER_LABEL = {
  color: '#9AA1AA',
  textTransform: 'uppercase',
  letterSpacing: '.08em',
  fontWeight: 600,
  fontSize: '10.5px',
  mb: 1,
  display: 'block',
};

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { logout, user, isDemoMode } = useAuth();
  const liveBooking = useLiveBooking();
  const { preferences } = usePreferences();
  // A dropdown over one item is a control that does nothing. Each switcher appears when there
  // is genuinely something to switch between, which is also the honest rule for somebody an
  // invitation has put into a second organisation without their ever answering a question about
  // it. Creating the second one is a different matter, and lives in Settings -> Workspace.
  const { organizations } = useOrganization();
  const { environments } = useEnvironment();
  const showOrgSwitcher = (organizations || []).length > 1;
  const showEnvSwitcher = (environments || []).filter((env) => !env.isArchived).length > 1;
  const showSwitchers = showOrgSwitcher || showEnvSwitcher;
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // A permanent 240px drawer on a 390px phone left 150px for the app itself. On a phone the
  // nav is an overlay opened from the bar; on a desktop it is a rail that expands.
  //
  // Width alone is not enough: a phone set to "request desktop site" reports ~980px and would
  // be handed the desktop layout, which is how it ends up looking zoomed out. A touch device
  // is a touch device whatever width it claims.
  const isTouch = useMediaQuery('(hover: none) and (pointer: coarse)');
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = isNarrow || isTouch;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => {
    try {
      return window.localStorage.getItem(EXPANDED_KEY) === 'true';
    } catch {
      // Private browsing can throw on access; collapsed is the default either way.
      return false;
    }
  });

  const setExpandedPersisted = (next) => {
    setExpanded(next);
    try {
      window.localStorage.setItem(EXPANDED_KEY, String(next));
    } catch {
      // Not worth failing navigation over a storage quota.
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Leads + Ad performance are the CTWA attribution features, switched off for now. Submissions
  // is not part of that suite, it's the plain record of flow submissions, independent of ads,
  // so it stays visible regardless of the flag.
  const navItems = [
    { path: '/flows', label: 'Flows', icon: <FlowsIcon /> },
    // Not for a demo account: submissions are other people's answers, and a demo builds in a
    // shared environment where "their own" is not a category that means anything. The server
    // refuses them either way (DemoRestrictionFilter); this is so nobody is offered a door that
    // will not open.
    ...(isDemoMode ? [] : [{ path: '/submissions', label: 'Submissions', icon: <ForumIcon /> }]),
    // The diary, for environments that have one. It was the bottom of a settings tab until
    // 2026-09-20: the screen somebody checks between customers, filed under configuration.
    ...(liveBooking ? [{ path: '/bookings', label: 'Bookings', icon: <BookingsIcon /> }] : []),
    ...(AD_ATTRIBUTION_ENABLED && preferences.adAttributionEnabled ? [
      { path: '/leads', label: 'Leads', icon: <InboxIcon /> },
      { path: '/attribution', label: 'Ad performance', icon: <InsightsIcon /> },
    ] : []),
    { path: '/settings', label: 'Settings', icon: <SettingsIcon /> },
  ];

  // Inside the mobile overlay everything is shown; on the desktop rail, labels and switchers
  // are hidden because there is no room for them.
  const drawerContent = (showLabels) => (
    <>
      <Box sx={{ p: showLabels ? 3 : 2, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.2, mb: showLabels ? 1 : 0 }}>
          <BrandMark size={28} />
          {showLabels && (
            <Typography variant="h5" component="div" sx={{ letterSpacing: '-0.02em' }}>
              <Box component="span" sx={{ fontWeight: 500, color: 'text.primary' }}>Flow</Box>
              <Box component="span" sx={{ fontWeight: 700, color: 'primary.main' }}>Gate</Box>
            </Typography>
          )}
        </Box>
        {showLabels && (
          <Typography variant="caption" sx={{
            color: '#9AA1AA',
            textTransform: 'uppercase',
            letterSpacing: '.08em',
            fontWeight: 600,
            fontSize: '10.5px'
          }}>
            Flow Builder
          </Typography>
        )}
      </Box>

      <Divider />

      {showLabels && showSwitchers && (
        <>
          <Box sx={{ px: 2, py: 2 }}>
            {showOrgSwitcher && (
              <>
                <Typography variant="caption" sx={SWITCHER_LABEL}>Organization</Typography>
                <OrganizationSwitcher />
              </>
            )}

            {showEnvSwitcher && (
              <>
                <Typography
                  variant="caption"
                  sx={{ ...SWITCHER_LABEL, mt: showOrgSwitcher ? 2 : 0 }}
                >
                  Environment
                </Typography>
                <EnvironmentSwitcher />
              </>
            )}
          </Box>
          <Divider />
        </>
      )}

      <List sx={{ px: 1, pt: 2, flexGrow: 1 }}>
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <Tooltip title={showLabels ? '' : item.label} placement="right">
                <ListItemButton
                  component={RouterLink}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  sx={{
                    borderRadius: 2.5,
                    // 48 gives a thumb something to hit; the default is under the 44 floor.
                    minHeight: 48,
                    justifyContent: showLabels ? 'flex-start' : 'center',
                    px: showLabels ? 2 : 1,
                    color: isActive ? '#4338CA' : '#3D434E',
                    bgcolor: isActive ? 'rgba(79, 70, 229, 0.09)' : 'transparent',
                    '&:hover': {
                      bgcolor: isActive ? 'rgba(79, 70, 229, 0.13)' : 'rgba(11, 13, 18, 0.04)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ListItemIcon sx={{
                    color: isActive ? '#4338CA' : '#697079',
                    minWidth: showLabels ? 38 : 0,
                    justifyContent: 'center',
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  {showLabels && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: 14.5,
                        fontWeight: isActive ? 600 : 500,
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>

      <Box sx={{ mt: 'auto', p: showLabels ? 2 : 1 }}>
        <Divider sx={{ mb: 2 }} />

        {showLabels && user && !isDemoMode && (
          <Typography variant="caption" sx={{
            color: 'text.secondary',
            mb: 1,
            display: 'block',
            textAlign: 'center',
            wordBreak: 'break-word'
          }}>
            {user.email}
          </Typography>
        )}

        {/* Somebody with no account yet. One nudge, in one place, saying what an account is for
            rather than what they are missing, and no countdown, no "demo mode", no flow tally. */}
        {showLabels && isDemoMode && (
          <Button
            fullWidth
            variant="contained"
            disableElevation
            onClick={() => navigate('/login')}
            sx={{ mb: 1, minHeight: 44 }}
          >
            Create an account
          </Button>
        )}

        {showLabels ? (
          <>
            <Button
              fullWidth
              variant="text"
              startIcon={<FeedbackIcon />}
              onClick={() => setFeedbackOpen(true)}
              sx={{ color: '#3D434E', mb: 1, minHeight: 44, '& .MuiSvgIcon-root': { color: '#697079' } }}
            >
              Send feedback
            </Button>

            {/* Not for a demo account. There is no account to log out OF, and the session is
                the only handle on the flows they have just built, "Logout" offers to throw
                that away and calls it something ordinary. The way out of a demo is to create
                an account, which is the button above. */}
            {!isDemoMode && (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={{
                  color: '#3D434E',
                  bgcolor: '#fff',
                  borderColor: 'divider',
                  minHeight: 44,
                  '& .MuiSvgIcon-root': { color: '#697079' },
                  '&:hover': {
                    borderColor: 'text.primary',
                    bgcolor: '#fff',
                  },
                }}
              >
                Logout
              </Button>
            )}
          </>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Send feedback" placement="right">
              <IconButton onClick={() => setFeedbackOpen(true)} sx={{ color: '#697079' }}>
                <FeedbackIcon />
              </IconButton>
            </Tooltip>
            {!isDemoMode && (
              <Tooltip title="Log out" placement="right">
                <IconButton onClick={handleLogout} sx={{ color: '#697079' }}>
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
    </>
  );

  const paperSx = (width) => ({
    width,
    boxSizing: 'border-box',
    bgcolor: '#FBFBFC',
    color: 'text.primary',
    borderRight: '1px solid',
    borderColor: 'divider',
    display: 'flex',
    flexDirection: 'column',
  });

  /**
   * A demo session has one screen (see DemoRoutes), so it gets no navigation at all.
   *
   * The rail's every item either went nowhere a demo account may go or opened a page explaining
   * why it was empty. What is left is what a stranger actually needs: the name of the product
   * they are trying, a way to tell us it is broken, and the one button the whole demo exists to
   * earn.
   */
  if (isDemoMode) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100dvh', overflow: 'hidden' }}>
        <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
        <AppBar
          position="static"
          color="inherit"
          elevation={0}
          sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#FBFBFC' }}
        >
          <Toolbar variant="dense" sx={{ minHeight: 56, gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9 }}>
              <BrandMark size={22} />
              <Typography component="div" sx={{ fontSize: 17, letterSpacing: '-0.02em' }}>
                <Box component="span" sx={{ fontWeight: 500 }}>Flow</Box>
                <Box component="span" sx={{ fontWeight: 700, color: 'primary.main' }}>Gate</Box>
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            {/* On a phone both labels wrapped to two lines and the bar doubled in height
                (2026-10-09): feedback becomes an icon, the account button a short label. */}
            {isMobile ? (
              <Tooltip title="Send feedback">
                <IconButton
                  size="small"
                  onClick={() => setFeedbackOpen(true)}
                  aria-label="Send feedback"
                  sx={{ color: 'text.secondary' }}
                  data-testid="demo-send-feedback"
                >
                  <FeedbackIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                size="small"
                color="inherit"
                onClick={() => setFeedbackOpen(true)}
                sx={{ color: 'text.secondary' }}
                data-testid="demo-send-feedback"
              >
                Send feedback
              </Button>
            )}
            <Button
              variant="contained"
              disableElevation
              size="small"
              onClick={() => navigate('/login')}
              sx={{ minHeight: 36, whiteSpace: 'nowrap' }}
              data-testid="demo-create-account"
            >
              {isMobile ? 'Sign up' : 'Create an account'}
            </Button>
          </Toolbar>
        </AppBar>
        <Box data-testid="page-scroll" sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto' }}>
          {children}
        </Box>
      </Box>
    );
  }

  return (
    // Height, not minHeight: the builder fills the screen and scrolls inside its own panes, so
    // an auto-height page meant `height: 100%` resolved to nothing and the chat sat below the
    // fold. dvh rather than vh so the phone's address bar does not cover the composer.
    <Box sx={{ display: 'flex', height: '100dvh', overflow: 'hidden' }}>
      {/* Two questions about how this account works, asked once over whatever page they
          arrived on, and never for a demo session. */}
      <WorkspaceSetupDialog />

      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': paperSx(drawerWidth) }}
        >
          {drawerContent(true)}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: expanded ? drawerWidth : railWidth,
            flexShrink: 0,
            transition: theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
            '& .MuiDrawer-paper': {
              ...paperSx(expanded ? drawerWidth : railWidth),
              overflowX: 'hidden',
              transition: theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
            },
          }}
        >
          {drawerContent(expanded)}
          <Box sx={{ display: 'flex', justifyContent: expanded ? 'flex-end' : 'center', p: 1 }}>
            <Tooltip title={expanded ? 'Collapse menu' : 'Expand menu'} placement="right">
              <IconButton
                size="small"
                aria-label={expanded ? 'Collapse menu' : 'Expand menu'}
                onClick={() => setExpandedPersisted(!expanded)}
              >
                {expanded ? <CollapseIcon /> : <ExpandIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Drawer>
      )}

      {/* No welcome dialog. The first thing a new account saw was a modal asking a question
          about ad tracking, which is a poor way to open a product whose answer is "describe
          the form you want". The app itself opens on that box; it explains itself better. */}
      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />

      {/* Mounted once, here, rather than per page: a 402 PLAN_LIMIT can come back from any
          request, and the plan-limit channel in the API client publishes it from one place.
          Features built after this one get the upgrade dialog for free. */}
      <PlanLimitDialog />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          bgcolor: 'background.default',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isMobile && (
          <AppBar position="static" color="inherit" elevation={0} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
            <Toolbar variant="dense" sx={{ minHeight: 52, gap: 1 }}>
              <IconButton edge="start" aria-label="Open menu" onClick={() => setMobileOpen(true)}>
                <MenuIcon />
              </IconButton>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9 }}>
                <BrandMark size={22} />
                <Typography component="div" sx={{ fontSize: 17, letterSpacing: '-0.02em' }}>
                  <Box component="span" sx={{ fontWeight: 500 }}>Flow</Box>
                  <Box component="span" sx={{ fontWeight: 700, color: 'primary.main' }}>Gate</Box>
                </Typography>
              </Box>
            </Toolbar>
          </AppBar>
        )}
        {/* The page region scrolls; the shell above it does not. The builder fills this box
            (height: 100%) and scrolls inside its own panes, so it never produces a scrollbar
            here, while every ordinary page (Settings, Submissions, Flows) gets normal scrolling
            back. `hidden` here clipped the Booking tab with no way to reach the bottom. */}
        <Box data-testid="page-scroll" sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto' }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
